import { Component, OnInit, OnDestroy, ViewChild, LOCALE_ID } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { TuiHostedDropdownComponent, TuiAlertService } from '@taiga-ui/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { EMPTY, firstValueFrom, Subscription, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  startWith,
  switchMap,
  tap
} from 'rxjs/operators';
import * as moment from 'moment';

import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';
import { Zone } from 'src/app/models/zone/zone.model';
import { Client } from 'src/app/models/service-detail/service-detail.model';
import {
  GetPaginatedServicesRequest,
  GetPaginatedServicesResponse,
  HistoricService,
  ZonedDate
} from 'src/app/models/historic-service/historic-service.model';
import { VerticalTypeTwo } from 'src/app/models/vertical-type/vertical-type.model';
import { FilterParams } from './interface/filter-params.model';
import {
  OrderStatusEnum,
  OrderStatusLabel
} from 'src/app/enum/order-status/order-status.enum';
import { verticalsTypes2 } from 'src/app/constants/data';
import { VerticalType } from 'src/app/enum/vertical-type/verticalType.enum';
import { VERTICAL_LABEL_KEY } from 'src/app/enum/vertical-type/vertical-lab.map';

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { LocalStorageService } from 'src/app/services/local-storage/local-storage.service';
import { LocalStorageKeys } from 'src/app/enum/local-storage-keys.enum';
import { ProductFilterItem } from 'src/app/models/pending-services/pending-services.model';
import { FiltersPersistenceService } from 'src/app/services/filters/filters-persistence.service';
import {
  VehicleFuelLabelKeys,
  VehicleFuelTypeEnum,
} from 'src/app/enum/vehicle-fuel.enum';
import { vehicleChasisTypeWithTranslations } from 'src/app/constants/data';
import {
  applyTimeFormat,
  DRIVER_STAGE_COLUMNS,
  getDriverStageTimes,
  timeToExcelDay
} from 'src/app/utils/driver-events.helper';

export interface HistoricServiceView extends HistoricService {
  displayDate?: string;
  displayTime?: string;
  driversNameDisplay?: string;
}

type SortableColumn = keyof HistoricService | 'date' | 'time' | 'driversNameDisplay';

@Component({
  selector: 'app-service-history',
  templateUrl: './service-history.component.html',
  styleUrls: ['./service-history.component.css']
})
export class ServiceHistoryComponent implements OnInit, OnDestroy {
  @ViewChild(TuiHostedDropdownComponent)
  dropdown?: TuiHostedDropdownComponent;

  filterForm!: FormGroup;
  private subs = new Subscription();

  zones: Zone[] = [];
  allClients: Client[] = [];

  paginatedData: HistoricServiceView[] = [];
  filteredData: HistoricServiceView[] = [];
  filterCount = 0;
  totalPages = 1;
  pageIndex = 0;
  readonly pageSize = 25;

  products: Array<ProductFilterItem & { _origProductKey?: string }> = [];

  loadingProducts = false;

  sortedColumn: SortableColumn | '' = '';
  sortDirection: 1 | -1 = -1;

  private originalPage: HistoricServiceView[] | null = null;

  isSearching = false;

  readonly columns: Array<
    | 'licensePlate'
    | 'orderHash'
    | 'orderStatus'
    | 'date'
    | 'time'
    | 'zoneName'
    | 'verticalType'
    | 'productName'
    | 'category'
    | 'clientName'
    | 'originAddress'
    | 'destinationAddress'
    | 'driversNameDisplay'
    | 'additionalInformation'
    | 'totalPrice'
    | 'actions'
  > = [
    'licensePlate',
    'orderHash',
    'orderStatus',
    'date',
    'time',
    'zoneName',
    'verticalType',
    'productName',
    'category',
    'clientName',
    'originAddress',
    'destinationAddress',
    'driversNameDisplay',
    'additionalInformation',
    'totalPrice',
    'actions'
  ];

  exportOpen = false;
  isCsvSelected = false;
  isXlsxSelected = false;
  hasFiltered = false;
  loading = false;
  showDateRangeRequired = false;

  exportCount = 0;
  exportLoading = false;

  readonly size: 's' | 'm' | 'l' = 'l';

  readonly verticals: VerticalTypeTwo[] = verticalsTypes2;

  orderStatus: Array<{ orderStatus: OrderStatusEnum; statusName: string }> = [];
  verticalOptions: Array<{ value: VerticalType; label: string }> = [];
  SERVICE_TYPE_TRANSLATION_KEY: Record<string | number, string> = {
    1: 'modals.add_service.option_home_mount',
    2: 'modals.add_service.option_no_mobility_with_mount',
    3: 'modals.add_service.option_shipping',
    4: 'modals.add_service.option_mobility_with_mount',
  };

  constructor(
    private readonly fb: FormBuilder,
    private readonly historyService: HistoryServicesService,
    private readonly alerts: TuiAlertService,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private localStorageService: LocalStorageService,
    private filtersPersistence: FiltersPersistenceService
  ) {
    this.buildForm();
  }

  ngOnInit(): void {
    this.buildVerticalOptions();
    this.subscribeToForm();
    this.loadLookups();
    this.buildOrderStatusList();
    this.changeLanguageAndUpdateFilters();
    this.getProductsFilter();
    this.restoreFilters();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.filtersPersistence.clear(LocalStorageKeys.SERVICEHISTORYFILTERS);
  }

  private buildForm(): void {
    const today = new Date();
    const raw = this.localStorageService.get(
      LocalStorageKeys.SERVICEHISTORYDATERANGE
    ) as string | null;

    let initialRange: [Date, Date];
    if (raw && typeof raw === 'string') {
      try {
        const [s, e] = JSON.parse(raw) as [string, string];
        initialRange = [new Date(s), new Date(e)];
      } catch {
        initialRange = [today, today];
      }
    } else {
      initialRange = [today, today];
    }

    this.filterForm = this.fb.group({
      search: [''],
      dateRange: [initialRange as [Date, Date]],
      zoneGuids: [[]],
      products: [[]],
      clientGuids: [[]],
      orderStatuses: [[]],
      verticalTypes: [[]]
    });
  }

  private changeLanguageAndUpdateFilters(): void {
    this.subs.add(
      this.translate.onLangChange.subscribe((_: LangChangeEvent) =>
        this.buildOrderStatusList()
      )
    );
  }

  private buildVerticalOptions(): void {
    this.verticalOptions = (
      Object.keys(VerticalType) as Array<keyof typeof VerticalType>
    )
      .filter((k) => !isNaN(Number(VerticalType[k])))
      .map((k) => {
        const code = VerticalType[k] as unknown as VerticalType;
        const i18nKey = VERTICAL_LABEL_KEY[code] ?? 'unknown';
        return {
          value: code,
          label: this.translate.instant(`enums.verticals.${i18nKey}`)
        };
      });
  }

  private buildOrderStatusList(): void {
    this.orderStatus = (
      Object.keys(OrderStatusEnum) as Array<keyof typeof OrderStatusEnum>
    )
      .filter((k) => !isNaN(Number(OrderStatusEnum[k])))
      .map((key) => {
        const code = OrderStatusEnum[key] as OrderStatusEnum;
        return {
          orderStatus: code,
          statusName: this.translate.instant(OrderStatusLabel[code])
        };
      });
  }

  private formatDisplayTime(time: string): string {
    return time.slice(0, 5);
  }

  private setLoading(state: boolean): void {
    this.loading = state;
    if (state) this.filterForm.disable({ emitEvent: false });
    else this.filterForm.enable({ emitEvent: false });
  }

  private subscribeToForm(): void {
    const dateCtrl = this.filterForm.get('dateRange')!;

    this.subs.add(
      dateCtrl.valueChanges
        .pipe(
          startWith(dateCtrl.value),
          debounceTime(250),
          tap(() => this.setLoading(true)),
          switchMap((range: [Date, Date] | null) => {
            if (!range || range.some((d) => !d)) {
              this.showDateRangeRequired = true;
              this.paginatedData = [];
              this.filteredData = [];
              this.filterCount = 0;
              this.setLoading(false);
              return EMPTY;
            }

            const [from, to] = range;

            if (moment(to).diff(moment(from), 'months', true) > 1) {
              this.alerts
                .open(
                  this.translate.instant(
                    'service_manager.history_service.max_date_range_exceeded',
                    { months: 1 }
                  ),
                  { status: 'error', autoClose: true }
                )
                .subscribe();

              const prev = this.historyService.historyDataParams?.date;
              if (prev) {
                dateCtrl.setValue(prev, { emitEvent: false });
              }

              this.setLoading(false);
              return EMPTY;
            }

            this.showDateRangeRequired = false;
            this.localStorageService.set(
              LocalStorageKeys.SERVICEHISTORYDATERANGE,
              JSON.stringify([from.toISOString(), to.toISOString()])
            );
            this.pageIndex = 0;
            this.loadPage(0);
            return EMPTY;
          })
        )
        .subscribe(),
      );
    const searchControl = this.filterForm.get('search')!;
    this.subs.add(
      searchControl.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(() => {
          this.pageIndex = 0;
          this.loadPage(0, {
            showLoader: false,
            disableForm: false,
            searchIndicator: true
          });
        })
    );

    const watch = (ctrl: string, delay = 300) =>
      this.subs.add(
        this.filterForm
          .get(ctrl)!
          .valueChanges.pipe(debounceTime(delay), distinctUntilChanged())
          .subscribe(() => {
            this.pageIndex = 0;
            this.loadPage(0);
            this.persistFilters();
          })
      );

    [
      'zoneGuids',
      'clientGuids',
      'products',
      'orderStatuses',
      'verticalTypes'
    ].forEach(watch);
  }

  async getProductsFilter() {
    this.loadingProducts = true;
    const fetched = await firstValueFrom(
      this.historyService.getProductsForFilters()
    );

    this.products = (fetched || []).map((product: ProductFilterItem) => {
      return {
        ...product,
        ProductLabel:
          this.translateProductKey(product.ProductKey) ||
          product.ProductName ||
          product.ProductLabel
      } as ProductFilterItem;
    });

    this.loadingProducts = false;
  }

  trackByServiceGuid(_index: number, item: HistoricServiceView): string {
    return item.serviceGuid;
  }

  private loadLookups(): void {
    this.setLoading(true);

    this.historyService.getZones().subscribe({
      next: (r) => (this.zones = r.zones),
      error: () => this.showError('history.zone_load_error')
    });

    this.historyService.getClients().subscribe({
      next: (c) => (this.allClients = c.businessUserData),
      error: () => this.showError('history.client_load_error')
    });

    this.setLoading(false);
  }

  loadPage(
    pageIndex: number,
    options?: {
      showLoader?: boolean;
      disableForm?: boolean;
      searchIndicator?: boolean;
    }
  ): void {
    const doShowLoader = options?.showLoader !== false;
    const doDisableForm = options?.disableForm !== false;
    const doSearchIndicator = options?.searchIndicator === true;

    const dateRange = this.filterForm.get('dateRange')!.value as
      | [Date, Date]
      | null;
    if (!dateRange || dateRange.some((d) => !d)) {
      this.paginatedData = [];
      this.filteredData = [];
      this.filterCount = 0;
      if (doShowLoader) this.setLoading(false);
      if (doSearchIndicator) this.isSearching = false;
      return;
    }

    const [fromD, toD] = dateRange;
    const fromStr = moment(fromD).format('YYYY-MM-DD');
    const toStr = moment(toD).format('YYYY-MM-DD');

    const raw = this.filterForm.getRawValue() as {
      search?: string | null;
      zoneGuids?: string[] | null;
      products?: string[] | null;
      clientGuids?: string[] | null;
      orderStatuses?: number[] | null;
      verticalTypes?: number[] | null;
    };

    const selectedProducts = Array.isArray(raw.products) ? raw.products : [];

    const productsNameForRequest = selectedProducts
      .map((selected) => {
        const found = this.products.find(
          (p) =>
            p.ProductName === selected ||
            p._origProductKey === selected ||
            p.ProductKey === selected
        );
        return found?._origProductKey ?? selected;
      })
      .filter(Boolean);

    const requestBody: GetPaginatedServicesRequest = {
      fromDate: fromStr,
      toDate: toStr,
      pageNumber: Number(pageIndex) + 1,
      pageSize: this.pageSize,
      zoneGuids: Array.isArray(raw.zoneGuids) ? raw.zoneGuids : [],
      productsName: productsNameForRequest,
      clientsGuid: Array.isArray(raw.clientGuids) ? raw.clientGuids : [],
      serviceStatus: Array.isArray(raw.orderStatuses)
        ? raw.orderStatuses.map(Number)
        : [],
      verticalTypes: Array.isArray(raw.verticalTypes)
        ? raw.verticalTypes.map(Number)
        : [],
      serviceFilter: (raw.search ?? '').toString().trim()
    };

    if (doShowLoader) this.setLoading(true);
    else {
      if (doDisableForm) this.filterForm.disable({ emitEvent: false });
    }
    if (doSearchIndicator) this.isSearching = true;

    this.historyService
      .getPaginatedHistoricServices(requestBody)
      .pipe(
        catchError((err) => {
          console.error('[ServiceHistory] getPaginatedServices error', err);
          this.showError('service_manager.history_service.load_error');
          return of({
            services: [],
            totalItems: 0,
            hasNextPage: false,
            hasPreviousPage: false
          } as GetPaginatedServicesResponse);
        }),
        finalize(() => {
          if (doShowLoader) this.setLoading(false);
          else if (doDisableForm) this.filterForm.enable({ emitEvent: false });
          if (doSearchIndicator) this.isSearching = false;
        })
      )
      .subscribe((resp: GetPaginatedServicesResponse) => {
        const services: HistoricService[] = Array.isArray(resp.services)
          ? resp.services
          : [];

        const mapped: (HistoricServiceView & { category?: string })[] = services.map((s) => {
          const displayDate = s.serviceDate?.completeDate
            ? this.formatDisplayDate(s.serviceDate.completeDate)
            : '';
          const displayTime = s.serviceDate?.time
            ? this.formatDisplayTime(s.serviceDate.time)
            : '';
          const driversNameArray = Array.isArray(s.driversName)
            ? s.driversName.filter((name) => !!name?.trim())
            : [];
          const driversNameDisplay = driversNameArray.length
            ? driversNameArray.join(', ')
            : (s.driverName?.trim() || '-');
          let category = '';
          const rawType = (s as any).serviceType;
          if (rawType && this.SERVICE_TYPE_TRANSLATION_KEY[rawType]) {
            category = this.translate.instant(this.SERVICE_TYPE_TRANSLATION_KEY[rawType]);
          }
          return {
            ...s,
            displayDate,
            displayTime,
            driversName: driversNameArray,
            driversNameDisplay,
            category
          } as HistoricServiceView & { category?: string };
        });

        this.originalPage = null;

        this.paginatedData = mapped;
        this.filteredData = mapped;
        const totalItems = Number(resp.totalItems ?? 0);
        this.filterCount = totalItems;
        this.totalPages = Math.max(1, Math.ceil(totalItems / this.pageSize));
        this.pageIndex = Math.min(pageIndex, Math.max(0, this.totalPages - 1));

        const anyFilterApplied =
          (requestBody.serviceFilter && requestBody.serviceFilter.length > 0) ||
          (requestBody.zoneGuids && requestBody.zoneGuids.length > 0) ||
          (requestBody.productsName && requestBody.productsName.length > 0) ||
          (requestBody.clientsGuid && requestBody.clientsGuid.length > 0) ||
          (requestBody.serviceStatus && requestBody.serviceStatus.length > 0) ||
          (requestBody.verticalTypes && requestBody.verticalTypes.length > 0);
        this.hasFiltered = Boolean(anyFilterApplied);

        if (this.sortedColumn) {
          this.applyLocalSortToPage();
        }
      });
  }

  shouldShowCategoryColumn(): boolean {
    return this.paginatedData.some(row => row.category && row.category !== '');
  }

  private applyLocalSortToPage(): void {
    if (!this.sortedColumn) return;
    const col = this.sortedColumn as SortableColumn;
    const dir = this.sortDirection;

    const compare = (a: HistoricServiceView, b: HistoricServiceView) => {
      if (col === 'date') {
        const aRaw = (a.serviceDate?.completeDate ?? '').split(' ')[0];
        const bRaw = (b.serviceDate?.completeDate ?? '').split(' ')[0];
        const normalize = (iso: string) => iso.replace(/([+-]\d{2})$/, '$1:00');
        const ta = Date.parse(normalize(aRaw)) || 0;
        const tb = Date.parse(normalize(bRaw)) || 0;
        return (ta - tb) * dir;
      }
      if (col === 'time') {
        const [h1 = 0, m1 = 0, s1 = 0] = (a.serviceDate?.time ?? '00:00:00')
          .split(':')
          .map(Number);
        const [h2 = 0, m2 = 0, s2 = 0] = (b.serviceDate?.time ?? '00:00:00')
          .split(':')
          .map(Number);
        const diff = (h1 - h2) * 3600 + (m1 - m2) * 60 + (s1 - s2);
        return diff * dir;
      }
      const ka = String((a as any)[col] ?? '');
      const kb = String((b as any)[col] ?? '');
      return (
        ka.localeCompare(kb, undefined, {
          sensitivity: 'base',
          numeric: true
        }) * dir
      );
    };

    this.paginatedData.sort(compare);
    this.filteredData = [...this.paginatedData];
  }

  onSort(col: SortableColumn): void {
    if (this.sortedColumn !== col) {
      if (this.originalPage == null) {
        this.originalPage = this.paginatedData
          ? this.paginatedData.slice()
          : [];
      }
      this.sortedColumn = col;
      this.sortDirection = 1;
      this.applyLocalSortToPage();
      return;
    }

    if (this.sortDirection === 1) {
      this.sortDirection = -1;
      this.applyLocalSortToPage();
      return;
    }

    this.sortedColumn = '';
    this.sortDirection = 1;
    if (this.originalPage != null) {
      this.paginatedData = this.originalPage.slice();
      this.filteredData = [...this.paginatedData];
    }
    this.originalPage = null;
  }

  setSortColumn(col: SortableColumn): void {
    this.onSort(col);
  }

  goToPage(idx: number): void {
    this.pageIndex = idx;
    this.loadPage(idx);
  }

  private persistFilters(): void {
    const values = this.filterForm.value;
    const params = {
      dateRange: Array.isArray(values.dateRange) ? values.dateRange : [new Date(), new Date()],
      zoneGuids: Array.isArray(values.zoneGuids) ? values.zoneGuids : [],
      products: Array.isArray(values.products) ? values.products : [],
      clientGuids: Array.isArray(values.clientGuids) ? values.clientGuids : [],
      orderStatuses: Array.isArray(values.orderStatuses) ? values.orderStatuses : [],
      verticalTypes: Array.isArray(values.verticalTypes) ? values.verticalTypes : [],
      search: typeof values.search === 'string' ? values.search : ''
    };
    this.filtersPersistence.save(LocalStorageKeys.SERVICEHISTORYFILTERS, params);
  }

  private formatDisplayDate(completeDate?: string | null): string {
    if (!completeDate) return '';
    const isoPart = String(completeDate).split(' ')[0];
    const m = moment(isoPart, moment.ISO_8601, true);
    return m.isValid() ? m.format('DD/MM/YYYY') : '';
  }

  private restoreFilters(): void {
    const today = new Date();
    const defaultFilters = {
      dateRange: [today, today],
      zoneGuids: [],
      products: [],
      clientGuids: [],
      orderStatuses: [],
      verticalTypes: [],
      search: ''
    };
    const filters = this.filtersPersistence.restore('service-history-filters', defaultFilters);
    this.filterForm.patchValue(filters, { emitEvent: false });
  }


  private showError(key: string): void {
    this.alerts
      .open(this.translate.instant(key), { status: 'error', autoClose: true })
      .subscribe();
  }

  viewDetail({ orderHash }: { orderHash?: string }): void {
    if (!orderHash) {
      console.warn('[ServiceHistory] viewDetail called without orderHash');
      return;
    }
    this.historyService.setData({ orderHash });
    const tree: UrlTree = this.router.createUrlTree(
      ['/service-manager/detail-service'],
      { queryParams: { orderHash } }
    );
    window.open(`${window.location.origin}/#/${tree}`, '_blank');
  }

  updateExportCount(): void {
    this.exportCount = Number(this.isCsvSelected) + Number(this.isXlsxSelected);
  }

  async exportTo(): Promise<void> {
    if (!this.exportCount) {
      this.alerts
        .open(
          this.translate.instant(
            'service_manager.history_service.export_no_format'
          ),
          {
            status: 'warning',
            autoClose: true
          }
        )
        .subscribe();
      return;
    }

    const dateRange = this.filterForm.get('dateRange')!.value as
      | [Date, Date]
      | null;
    if (!dateRange || dateRange.some((d) => !d)) {
      this.alerts
        .open(
          this.translate.instant(
            'service_manager.history_service.date_range_required'
          ),
          { status: 'error', autoClose: true }
        )
        .subscribe();
      return;
    }

    this.exportLoading = true;

    try {
      const [fromD, toD] = dateRange;
      const fromStr = moment(fromD).format('YYYY-MM-DD');
      const toStr = moment(toD).format('YYYY-MM-DD');

      const raw = this.filterForm.getRawValue() as {
        search?: string | null;
        zoneGuids?: string[] | null;
        products?: string[] | null;
        clientGuids?: string[] | null;
        orderStatuses?: number[] | null;
        verticalTypes?: number[] | null;
      };

      const selectedProducts = Array.isArray(raw.products) ? raw.products : [];

      const productsNameForRequest = selectedProducts
        .map((selected) => {
          const found = this.products.find(
            (p) =>
              p.ProductName === selected ||
              p._origProductKey === selected ||
              p.ProductKey === selected
          );
          return found?._origProductKey ?? selected;
        })
        .filter(Boolean);

      const paramsForAll = {
        fromDate: fromStr,
        toDate: toStr,
        zoneGuids: Array.isArray(raw.zoneGuids) ? raw.zoneGuids : [],
        productsName: productsNameForRequest,
        clientsGuid: Array.isArray(raw.clientGuids) ? raw.clientGuids : [],
        serviceStatus: Array.isArray(raw.orderStatuses)
          ? raw.orderStatuses.map(Number)
          : [],
        verticalTypes: Array.isArray(raw.verticalTypes)
          ? raw.verticalTypes.map(Number)
          : [],
        serviceFilter: (raw.search ?? '').toString().trim()
      };

      // call backend to fetch ALL services (no pagination)
      const allServices = await firstValueFrom(
        this.historyService.getAllHistoricServices(paramsForAll)
      );

      const mapped: HistoricServiceView[] = (allServices || []).map((s) => {
        const displayDate = s.serviceDate?.completeDate
          ? this.formatDisplayDate(s.serviceDate.completeDate)
          : '';
        const displayTime = s.serviceDate?.time
          ? this.formatDisplayTime(s.serviceDate.time)
          : '';
        return {
          ...s,
          displayDate,
          displayTime
        } as HistoricServiceView;
      });

      if (!mapped.length) {
        this.alerts
          .open(
            this.translate.instant(
              'service_manager.history_service.no_services_found'
            ),
            { status: 'warning', autoClose: true }
          )
          .subscribe();
        return;
      }

      if (this.isCsvSelected) {
        this.exportCSVFrom(mapped);
      }
      if (this.isXlsxSelected) {
        this.exportXLSXFrom(mapped);
      }

      this.alerts
        .open(
          this.translate.instant('service_manager.history_service.export_done'),
          { status: 'success', autoClose: true }
        )
        .subscribe();
    } catch (err) {
      console.error('[ServiceHistory] exportTo error', err);
      this.alerts
        .open(
          this.translate.instant(
            'service_manager.history_service.export_error'
          ),
          { status: 'error', autoClose: true }
        )
        .subscribe();
    } finally {
      this.exportLoading = false;
      this.exportOpen = false;
      this.isCsvSelected = false;
      this.isXlsxSelected = false;
      this.updateExportCount();
    }
  }

  private verticalKey(v: number): string {
    return VERTICAL_LABEL_KEY[v as VerticalType] ?? 'unknown';
  }

  private exportXLSXFrom(services: HistoricServiceView[]): void {
    const t = this.translate;
    const today = moment().tz('Europe/Madrid').format('DD/MM/YYYY');

    const nameTpl = t.instant(
      'service_manager.history_service.export_filename',
      { date: today }
    );
    const filename = nameTpl.replace(/\.csv$/i, '.xlsx');

    const headers = [
      t.instant('service_manager.history_service.plate_input_table'),
      t.instant('service_manager.history_service.hash_input_table'),
      t.instant('service_manager.history_service.status_input_table'),
      t.instant('service_manager.history_service.date_input_table'),
      t.instant('service_manager.history_service.hour_input_table'),
      t.instant('service_manager.history_service.zone_input_table'),
      t.instant('service_manager.history_service.vertical_input_table'),
      t.instant('service_manager.history_service.product_input_table'),
      t.instant('service_manager.history_service.client_input_table'),
      t.instant('service_manager.history_service.phoneNumber_input_table'),
      t.instant('service_manager.history_service.pickup_address_input_table'),
      t.instant('service_manager.history_service.provider_address_input_table'),
      t.instant('service_manager.history_service.delivery_input_table'),
      t.instant('service_manager.history_service.comments_input_table'),
      t.instant('service_manager.history_service.custom_fields_input_table'),
      t.instant('service_manager.history_service.tags_input_table'),
      t.instant('service_manager.history_service.km_input_table'),
      t.instant(
        'service_manager.history_service.favorite_pickup_address_input_table'
      ),
      t.instant(
        'service_manager.history_service.favorite_delivery_address_input_table'
      ),
      t.instant('service_manager.history_service.node_hour_input_table'),
      t.instant('service_manager.history_service.additional_cost_input_table'),
      t.instant('service_manager.history_service.vehicle_type_input_table'),
      t.instant('service_manager.history_service.vehicle_brand_input_table'),
      t.instant('service_manager.history_service.vehicle_model_input_table'),
      t.instant(
        'service_manager.history_service.vehicle_license_plate_input_table'
      ),
      t.instant('service_manager.history_service.vehicle_fuel_type_input_table'),
      t.instant('service_manager.history_service.product_price'),
      t.instant('service_manager.history_service.mobility_price'),
      t.instant('service_manager.history_service.urgency_fee'),
      t.instant('service_manager.history_service.driver_name'),
      t.instant('service_manager.history_service.approved_expenses_input_table'),
      t.instant('service_manager.history_service.totalPrice_input_table'),
      t.instant('service_manager.history_service.price_without_vat'),
      t.instant('service_manager.history_service.pool_price'),
      t.instant('service_manager.history_service.delay_reason'),
      t.instant('service_manager.history_service.cancellation_reason'),
      t.instant('service_manager.history_service.failed_reason'),
      t.instant('service_manager.history_service.driver_pickup_start'),
      t.instant('service_manager.history_service.driver_pickup_end'),
      t.instant('service_manager.history_service.driver_return_start'),
      t.instant('service_manager.history_service.driver_return_end')
    ];

    const body = services.map((s) => {
      const currency = s.currencySymbol ?? '';
      const fmt = (n?: number | null) =>
        n != null ? `${n.toFixed(2)}${currency}` : '';

      const row = [
        s.licensePlate ?? '',
        s.orderHash ?? '',
        t.instant(OrderStatusLabel[s.orderStatus ?? 0] ?? ''),
        s.displayDate ?? '',
        s.displayTime ?? '',
        s.zoneName ?? '',
        t.instant('enums.verticals.' + this.verticalKey(s.verticalType ?? 0)),
        this.serviceTypeLabel(s.productName ?? ''),
        s.clientName ?? '',
        s.phoneNumber ?? '',
        s.originAddress ?? '',
        s.providerAddress ?? '',
        s.destinationAddress ?? '',
        s.additionalInformation ?? '',
        this.getCustomFieldsValue(s),
        this.getTagsValue(s),
        this.getKmValue(s),
        this.getFavoritePickupAddressValue(s),
        this.getFavoriteDeliveryAddressValue(s),
        this.getNodeHourValue(s),
        this.getAdditionalCostValue(s),
        this.getVehicleTypeValue(s),
        this.getVehicleBrandValue(s),
        this.getVehicleModelValue(s),
        this.getVehicleLicensePlateValue(s),
        this.getVehicleFuelTypeValue(s),
        fmt(s.productPrice),
        fmt(s.mobilityPrice),
        fmt(s.urgencyFee),
        s.driverName ?? '',
        fmt(s.approvedExpensesAmount),
        fmt(s.totalPrice),
        fmt(s.totalPriceWithoutTax),
        s.isAvailableForFreelancePool ? fmt(s.freelanceCustomPrice ?? 0) : '',
        s.delayDescription ?? '',
        s.cancellationDescription ?? '',
        s.failureDescription ?? ''
      ];
      // Horas del conductor como valor de hora (no texto) para que Excel pueda restar inicio y fin.
      const stageTimes = getDriverStageTimes(s.driverEvents).map(
        (time) => timeToExcelDay(time) ?? '-'
      );
      return [...row.map((cell) => this.dashIfEmpty(cell)), ...stageTimes];
    });

    const aoa = [headers, ...body];
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(aoa);
    applyTimeFormat(
      ws,
      headers.length - DRIVER_STAGE_COLUMNS,
      DRIVER_STAGE_COLUMNS
    );

    ws['!cols'] = [
      { wch: 10 }, // Matrícula
      { wch: 10 }, // Hash
      { wch: 12 }, // Estado
      { wch: 12 }, // Fecha
      { wch: 8 }, // Hora
      { wch: 13 }, // Zona
      { wch: 15 }, // Vertical
      { wch: 18 }, // Producto
      { wch: 15 }, // Cliente
      { wch: 25 }, // Teléfono
      { wch: 40 }, // Dirección origen
      { wch: 40 }, // Dirección proveedor
      { wch: 45 }, // Dirección destino
      { wch: 40 }, // Comentarios
      { wch: 30 }, // Campos personalizados
      { wch: 30 }, // Tags
      { wch: 10 }, // KM
      { wch: 30 }, // Dirección recogida favorita
      { wch: 30 }, // Dirección entrega favorita
      { wch: 12 }, // Hora nodo
      { wch: 14 }, // Coste adicional
      { wch: 16 }, // Tipo vehículo
      { wch: 16 }, // Marca vehículo
      { wch: 16 }, // Modelo vehículo
      { wch: 18 }, // Matrícula vehículo
      { wch: 16 }, // Tipo combustible
      { wch: 10 }, // Precio producto
      { wch: 10 }, // Precio movilidad
      { wch: 10 }, // Recargo urgencia
      { wch: 26 }, // Conductor
      { wch: 16 }, // Gastos aprobados
      { wch: 10 }, // Precio
      { wch: 10 }, // Precio sin IVA
      { wch: 14 }, // Precio pool
      { wch: 30 }, // Motivo retraso
      { wch: 30 }, // Motivo cancelación
      { wch: 30 }, // Motivo fallido
      { wch: 16 }, // Inicio recogida
      { wch: 16 }, // Fin recogida
      { wch: 16 }, // Inicio devolución
      { wch: 16 } // Fin devolución
    ];

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Histórico');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([wbout], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }),
      filename
    );
  }

  private escapeCsv = (val: unknown): string => {
    if (val === undefined || val === null) return '';
    const s = String(val);
    const safe = s.replace(/"/g, '""');
    return /[",\r\n]/.test(s) ? `"${safe}"` : safe;
  };

  private dashIfEmpty(value: unknown): string {
    if (value === null || value === undefined) return '-';
    const normalized = String(value).trim();
    return normalized.length ? normalized : '-';
  }

  private firstNonEmptyValue(values: unknown[]): string {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      if (typeof value === 'number') return String(value);
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    return '';
  }

  private getMetadataItems(
    service: HistoricServiceView
  ): Array<{ id: string; tag: string }> {
    const raw = service as any;
    const list = Array.isArray(raw.metadataTags)
      ? raw.metadataTags
      : Array.isArray(raw.MetadataTags)
      ? raw.MetadataTags
      : Array.isArray(raw.Tags)
      ? raw.Tags
      : Array.isArray(raw.tags)
      ? raw.tags
      : [];

    return list.map((item: any) => ({
      id:
        typeof item?.id === 'string'
          ? item.id.trim()
          : typeof item?.Id === 'string'
          ? item.Id.trim()
          : '',
      tag:
        typeof item?.tag === 'string'
          ? item.tag.trim()
          : typeof item?.Tag === 'string'
          ? item.Tag.trim()
          : '',
    }));
  }

  // Campos personalizados: elementos cuyo "tag" tiene contenido. Formato "{id}: {tag}", uno por línea.
  private getCustomFieldsValue(service: HistoricServiceView): string {
    return this.getMetadataItems(service)
      .filter((item) => item.tag.length > 0)
      .map((item) => `${item.id}: ${item.tag}`)
      .join('\n');
  }

  // Tags: elementos cuyo "tag" es cadena vacía. Se concatena el "id" separado por comas.
  private getTagsValue(service: HistoricServiceView): string {
    return this.getMetadataItems(service)
      .filter((item) => item.tag.length === 0)
      .map((item) => item.id)
      .filter((id) => id.length > 0)
      .join(', ');
  }

  private getKmValue(service: HistoricServiceView): string {
    const raw = service as any;
    const meters = Number(raw.distanceInMeters);
    if (!Number.isFinite(meters)) return '';

    const km = meters / 1000;
    return Number.isInteger(km) ? String(km) : km.toFixed(2);
  }

  private getFavoritePickupAddressValue(service: HistoricServiceView): string {
    const raw = service as any;
    return this.firstNonEmptyValue([raw.clientStartAddressKey]);
  }

  private getFavoriteDeliveryAddressValue(service: HistoricServiceView): string {
    const raw = service as any;
    return this.firstNonEmptyValue([raw.clientFinishAddressKey]);
  }

  private getNodeHourValue(service: HistoricServiceView): string {
    const raw = service as any;
    const nodeDates = Array.isArray(raw.NodeDates)
      ? raw.NodeDates
      : Array.isArray(raw.nodeDates)
      ? raw.nodeDates
      : [];

    const candidate = this.firstNonEmptyValue([
      ...nodeDates.map(
        (node: any) =>
          node?.AssignedStartDate?.time ?? node?.assignedStartDate?.time
      ),
      ...nodeDates.map(
        (node: any) =>
          node?.AssignedStartDate?.completeDate ??
          node?.assignedStartDate?.completeDate
      ),
      raw.NodeDates?.AssignedStartDate?.time,
      raw.NodeDates?.assignedStartDate?.time,
      raw.nodeDates?.AssignedStartDate?.time,
      raw.nodeDates?.assignedStartDate?.time,
    ]);

    if (!candidate) return '';

    if (typeof candidate === 'string') {
      const asHour = moment(candidate, ['HH:mm:ss', 'HH:mm'], true);
      if (asHour.isValid()) return asHour.format('HH:mm');

      const asDate = moment(candidate);
      if (asDate.isValid()) return asDate.format('HH:mm');

      return candidate;
    }

    return '';
  }

  private getAdditionalCostValue(service: HistoricServiceView): string {
    const raw = service as any;
    const currency = service.currencySymbol ?? '';
    const purchasedServicesInfo = Array.isArray(raw.purchasedServicesInfo)
      ? raw.purchasedServicesInfo
      : [];

    const overdraftItems = purchasedServicesInfo.filter(
      (item: any) => item?.localizationKey === 'overdraft'
    );
    if (!overdraftItems.length) return '';

    const total = overdraftItems.reduce((sum: number, item: any) => {
      const price = Number(item?.price ?? 0);
      return Number.isFinite(price) ? sum + price : sum;
    }, 0);

    return `${total.toFixed(2)}${currency}`;
  }

  private getVehicleTypeValue(service: HistoricServiceView): string {
    const raw = service as any;
    const rawType = raw.vehicleChassisType ?? raw.vehicleChasisType;
    const typeId = Number(rawType);

    if (Number.isFinite(typeId)) {
      const match = vehicleChasisTypeWithTranslations.find(
        (item) => item.value === typeId
      );
      if (match?.real_name) {
        return this.translate.instant(match.real_name);
      }
    }

    return this.firstNonEmptyValue([rawType, raw.vehicleCategory]);
  }

  private getVehicleBrandValue(service: HistoricServiceView): string {
    const raw = service as any;
    return this.firstNonEmptyValue([raw.vehicleBrand]);
  }

  private getVehicleModelValue(service: HistoricServiceView): string {
    const raw = service as any;
    return this.firstNonEmptyValue([raw.vehicleModel]);
  }

  private getVehicleLicensePlateValue(service: HistoricServiceView): string {
    const raw = service as any;
    return this.firstNonEmptyValue([raw.vehicleLicensePlate]);
  }

  private getVehicleFuelTypeValue(service: HistoricServiceView): string {
    const raw = service as any;
    const rawFuel = raw.vehicleFuelType;
    const fuelId = Number(rawFuel);

    if (Number.isFinite(fuelId)) {
      const labelKey = VehicleFuelLabelKeys[fuelId as VehicleFuelTypeEnum];
      if (labelKey) {
        return this.translate.instant(labelKey);
      }
    }

    return this.firstNonEmptyValue([rawFuel]);
  }

  private exportCSVFrom(services: HistoricServiceView[]): void {
    const t = this.translate;
    const today = moment().tz('Europe/Madrid').format('DD-MM-YYYY');

    const csvTpl = t.instant(
      'service_manager.history_service.export_filename',
      { date: today }
    );
    const filename = csvTpl.endsWith('.csv') ? csvTpl : `${csvTpl}.csv`;

    const header = [
      t.instant('service_manager.history_service.plate_input_table'),
      t.instant('service_manager.history_service.hash_input_table'),
      t.instant('service_manager.history_service.status_input_table'),
      t.instant('service_manager.history_service.date_input_table'),
      t.instant('service_manager.history_service.hour_input_table'),
      t.instant('service_manager.history_service.zone_input_table'),
      t.instant('service_manager.history_service.vertical_input_table'),
      t.instant('service_manager.history_service.product_input_table'),
      t.instant('service_manager.history_service.client_input_table'),
      t.instant('service_manager.history_service.phoneNumber_input_table'),
      t.instant('service_manager.history_service.pickup_address_input_table'),
      t.instant('service_manager.history_service.provider_address_input_table'),
      t.instant('service_manager.history_service.delivery_input_table'),
      t.instant('service_manager.history_service.comments_input_table'),
      t.instant('service_manager.history_service.custom_fields_input_table'),
      t.instant('service_manager.history_service.tags_input_table'),
      t.instant('service_manager.history_service.km_input_table'),
      t.instant(
        'service_manager.history_service.favorite_pickup_address_input_table'
      ),
      t.instant(
        'service_manager.history_service.favorite_delivery_address_input_table'
      ),
      t.instant('service_manager.history_service.node_hour_input_table'),
      t.instant('service_manager.history_service.additional_cost_input_table'),
      t.instant('service_manager.history_service.vehicle_type_input_table'),
      t.instant('service_manager.history_service.vehicle_brand_input_table'),
      t.instant('service_manager.history_service.vehicle_model_input_table'),
      t.instant(
        'service_manager.history_service.vehicle_license_plate_input_table'
      ),
      t.instant('service_manager.history_service.vehicle_fuel_type_input_table'),
      t.instant('service_manager.history_service.product_price'),
      t.instant('service_manager.history_service.mobility_price'),
      t.instant('service_manager.history_service.urgency_fee'),
      t.instant('service_manager.history_service.driver_name'),
      t.instant('service_manager.history_service.approved_expenses_input_table'),
      t.instant('service_manager.history_service.totalPrice_input_table'),
      t.instant('service_manager.history_service.price_without_vat'),
      t.instant('service_manager.history_service.pool_price'),
      t.instant('service_manager.history_service.delay_reason'),
      t.instant('service_manager.history_service.cancellation_reason'),
      t.instant('service_manager.history_service.failed_reason'),
      t.instant('service_manager.history_service.driver_pickup_start'),
      t.instant('service_manager.history_service.driver_pickup_end'),
      t.instant('service_manager.history_service.driver_return_start'),
      t.instant('service_manager.history_service.driver_return_end')
    ]
      .map(this.escapeCsv)
      .join(',');

    const rows = services.map((s) => {
      const currency = s.currencySymbol ?? '';
      const fmt = (n?: number | null) =>
        n != null ? `${n.toFixed(2)}${currency}` : '';

      const cols = [
        s.licensePlate ?? '',
        s.orderHash ?? '',
        t.instant(OrderStatusLabel[s.orderStatus ?? 0] ?? ''),
        s.displayDate ?? '',
        s.displayTime ?? '',
        s.zoneName ?? '',
        t.instant('enums.verticals.' + this.verticalKey(s.verticalType ?? 0)),
        this.serviceTypeLabel(s.productName ?? ''),
        s.clientName ?? '',
        s.phoneNumber ?? '',
        s.originAddress ?? '',
        s.providerAddress ?? '',
        s.destinationAddress ?? '',
        s.additionalInformation ?? '',
        this.getCustomFieldsValue(s),
        this.getTagsValue(s),
        this.getKmValue(s),
        this.getFavoritePickupAddressValue(s),
        this.getFavoriteDeliveryAddressValue(s),
        this.getNodeHourValue(s),
        this.getAdditionalCostValue(s),
        this.getVehicleTypeValue(s),
        this.getVehicleBrandValue(s),
        this.getVehicleModelValue(s),
        this.getVehicleLicensePlateValue(s),
        this.getVehicleFuelTypeValue(s),
        fmt(s.productPrice),
        fmt(s.mobilityPrice),
        fmt(s.urgencyFee),
        s.driverName ?? '',
        fmt(s.approvedExpensesAmount),
        fmt(s.totalPrice),
        fmt(s.totalPriceWithoutTax),
        s.isAvailableForFreelancePool ? fmt(s.freelanceCustomPrice ?? 0) : '',
        s.delayDescription ?? '',
        s.cancellationDescription ?? '',
        s.failureDescription ?? '',
        ...getDriverStageTimes(s.driverEvents)
      ];
      return cols
        .map((cell) => this.escapeCsv(this.dashIfEmpty(cell)))
        .join(',');
    });

    const csvContent = [header, ...rows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, filename);
  }

  get index(): number {
    return this.pageIndex;
  }
  get length(): number {
    return this.totalPages;
  }

  translateProductKey(name: string): string {
    return this.historyService.productsCodes[this.translate.currentLang][name];
  }

  translateProduct(name: string): string {
    const key = 'common.services.' + name;
    const translated = this.translate.instant(key);
    return translated === key ? name : translated;
  }

  // Accept undefined to avoid template type errors
  serviceTypeLabel(productName?: string): string {
    if (!productName) return '';
    const key = 'common.services.' + productName;
    const translated = this.translate.instant(key);
    return translated === key ? productName : translated;
  }
}
